import React from "react";

import { Box, Flex, Text, Button, Image, Divider, Spinner, Radio, RadioGroup, Stack } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import paystackLogo from "../images/paystack-logo-vector.svg";
import { BankTransferDetails } from "./BankTransferDetails";
import { PAYMENT_UNAVAILABLE, PARTIAL_COUPON_BANK_MESSAGE, blockingReasonMessages } from "../../../utils/residentPaymentSettings";
import { commodityUnit } from "../../../utils/commodityUnits";

const nairaFmt = (n) =>
  `₦${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ReviewsAndPayments = ({
  handleSubmit,
  paymentLoading,
  userProfile,
  meterId,
  quote,
  quoteLoading,
  quoteError,
  appliedCoupons,
  isBankTransfer,
  bankTransfer,
  paymentSettings = {},
  paymentMethod,
  setChosenPaymentMethod,
  commodity = "water",
  unitLabel,
}) => {
  const pageColor = useSelector(
    (state) => state?.switchTab?.brandColor?.brandColor,
  );

  // Checkpoint 2C — this page displays the server-authoritative quote only.
  // The amount shown here immediately before "Buy" is exactly what
  // /order/initiate will attempt to charge (subject to its own final
  // revalidation) — never a locally-recomputed total.
  const fullyCovered = quote?.fullyCoveredByCoupon === true && quote?.payableAmount === 0;
  const buyLabel = fullyCovered ? "Complete order" : "Buy";
  const canSubmit = !!quote && !quoteLoading && !quoteError && (fullyCovered || (paymentMethod === "PAYSTACK" && paymentSettings.paystackAvailable));
  const bankSelected = !fullyCovered && isBankTransfer && paymentSettings.bankTransferAvailable;
  const partialBank = bankSelected && (quote?.appliedCoupons?.length > 0 || appliedCoupons?.length > 0);
  const unavailable = !paymentSettings.paystackAvailable && !paymentSettings.bankTransferAvailable;
  const bothRails = paymentSettings.paystackAvailable && paymentSettings.bankTransferAvailable;
  const reasonLines = blockingReasonMessages(paymentSettings.blockingReasons);
  const unit = commodityUnit(commodity, quote?.unit || unitLabel);

  return (
    <Box width="100%" p={["", "", "50px 30px"]}>
      <Flex
        direction={["column", "column", "row"]}
        bg="#F9FEFF"
        width="100%"
        justifyContent="space-between"
        px="30px"
        borderRadius="20px"
      >
        <Flex
          direction="column"
          width={["100%", "100%", "50%"]}
          color="#050509"
          py="50px"
          pr={[0, "10%"]}
        >
          {!fullyCovered && bothRails && (
            <Box mb="25px">
              <Text fontWeight="semibold" mb="10px">Choose how to pay</Text>
              <RadioGroup value={paymentMethod} onChange={setChosenPaymentMethod}>
                <Stack>
                  <Radio value="PAYSTACK">Pay Online</Radio>
                  <Radio value="BANK_TRANSFER">Bank Transfer</Radio>
                </Stack>
              </RadioGroup>
            </Box>
          )}
          {!fullyCovered && unavailable ? (
            <Box>
              <Text fontWeight="semibold">{PAYMENT_UNAVAILABLE}</Text>
              {reasonLines.map((line) => <Text key={line} fontSize="13px" color="gray.600">{line}</Text>)}
            </Box>
          ) : partialBank ? (
            <Text>{PARTIAL_COUPON_BANK_MESSAGE}</Text>
          ) : bankSelected ? (
            !quoteLoading && !quoteError && quote && <BankTransferDetails bankTransfer={bankTransfer} amount={quote.utilityAmount} />
          ) : (fullyCovered || (paymentMethod === "PAYSTACK" && paymentSettings.paystackAvailable)) ? (
            <>
              <Box bg="#fff" border="1px solid #f2f2f2" height="100px">
                <Flex alignItems="center" my="10px" px="20px">
                  {!fullyCovered && <Image width="120px" src={paystackLogo} alt={"paystack logo"} />}
                  <Text color="#10112B" ml="4px" fontWeight="semibold">
                    {fullyCovered ? "Coupon" : "Paystack"}
                  </Text>
                </Flex>
              </Box>
              <Box my="30px">
                <Button
                  width="100%"
                  bg={pageColor}
                  color="#fff"
                  px="20px"
                  _hover={{ bg: pageColor }}
                  onClick={handleSubmit}
                  isLoading={paymentLoading}
                  isDisabled={!canSubmit}
                >
                  {buyLabel}
                </Button>
              </Box>
            </>
          ) : <Text>Choose a payment method to continue.</Text>}
        </Flex>
        <Box
          width={["100%", "100%", "50%"]}
          p="50px 30px"
          borderLeft={["none", "none", "1px solid #d4d4d4"]}
          borderTop={["1px solid #d4d4d4", "1px solid #d4d4d4", "none"]}
        >
          <Box>
            <Text fontSize="14px">Details</Text>
            <Text fontWeight="semibold" my="1px">
              {userProfile?.name}
            </Text>
            <Text fontSize="14px">{userProfile?.houseId?.houseNumber}</Text>
            <Text fontSize="14px" my="1px">
              {userProfile?.phoneNumber}
            </Text>
          </Box>
          <Box my="25px">
            <Text fontSize="14px">Meter Details</Text>
            <Text fontWeight="semibold" my="1px">
              {meterId}
            </Text>
          </Box>
          <Divider />

          {quoteLoading && !quote && (
            <Flex align="center" justify="center" py="20px">
              <Spinner size="sm" color={pageColor} mr="2" />
              <Text fontSize="13px" color="gray.600">
                Calculating your total…
              </Text>
            </Flex>
          )}

          {quoteError && (
            <Text fontSize="13px" color="red.500" my="10px">
              {quoteError}
            </Text>
          )}

          {quote && !partialBank && (
            <>
              <Flex
                width="100%"
                justifyContent="space-between"
                my="10px"
                fontWeight="semibold"
              >
                <Text>Utility purchase</Text>
                <Text>{nairaFmt(quote.utilityAmount)}</Text>
              </Flex>
              <Divider />
              <Flex
                width="100%"
                justifyContent="space-between"
                my="10px"
                fontSize="14px"
              >
                <Text>Service charge</Text>
                <Text fontWeight="semibold">{nairaFmt(bankSelected ? 0 : quote.serviceFee)}</Text>
              </Flex>
              <Divider />
              <Flex
                width="100%"
                justifyContent="space-between"
                my="10px"
                fontSize="14px"
              >
                <Text>Gross total</Text>
                <Text fontWeight="semibold">{nairaFmt(bankSelected ? quote.utilityAmount : quote.grossAmount)}</Text>
              </Flex>

              {quote.appliedCoupons?.length > 0 && (
                <>
                  <Divider />
                  {quote.appliedCoupons.map((c) => (
                    <Flex
                      key={c.code}
                      width="100%"
                      justifyContent="space-between"
                      my="10px"
                      fontSize="14px"
                    >
                      <Text color="#2A9D8F">Coupon {c.code}</Text>
                      <Text color="#2A9D8F" fontWeight="semibold">
                        -{nairaFmt(c.valueInNaira)}
                      </Text>
                    </Flex>
                  ))}
                </>
              )}

              <Divider />
              <Flex
                width="100%"
                justifyContent="space-between"
                my="10px"
                fontWeight="bold"
              >
                <Text>Amount to pay</Text>
                <Text color={fullyCovered ? "#2A9D8F" : "inherit"}>
                  {fullyCovered ? "Fully covered by coupon" : nairaFmt(bankSelected ? quote.utilityAmount : quote.payableAmount)}
                </Text>
              </Flex>
              <Divider />

              <Flex width="100%" justifyContent="space-between" my="10px" fontSize="13px">
                <Text color="#666">{bankSelected ? "Estimated units" : "Units"}</Text>
                <Text fontWeight="semibold">
                  {Number(quote.unitPurchased).toFixed(2)} {unit}
                </Text>
              </Flex>
              <Flex width="100%" justifyContent="space-between" fontSize="13px">
                <Text color="#666">Rate</Text>
                <Text fontWeight="semibold">{nairaFmt(quote.rateApplied)}/{unit}</Text>
              </Flex>
            </>
          )}
        </Box>
      </Flex>
    </Box>
  );
};
